/**
 * Оформление графика: фон, цвета свечей, сетка, объём.
 *
 * Человек проводит на графике часы, поэтому вид полотна — его выбор:
 * готовые виды, свои цвета свечей и даже своя картинка на фоне. По
 * умолчанию — «Ночь»: плоский тёмно-синий фон, сетка линиями и яркие
 * свечи — вид, который выбрал владелец.
 *
 * Полотно Lightweight Charts рисуется на холсте и градиентов с
 * картинками не умеет, поэтому сам холст прозрачен, а фон — обычный CSS
 * на его контейнере. Цвета свечей и сетки уходят в `palette`: её читают
 * все слои графика, и свечи, разметка и отметки остаются в одном тоне.
 *
 * Вид лежит в профиле человека (рабочее место) и едет на все его
 * устройства. Своя картинка — нет: она хранится в браузере, потому что
 * профиль не место для сотен килобайт. На другом устройстве вместо неё
 * будет фон выбранного вида.
 */

import { palette } from './theme.js';

export type LookGrid = 'dots' | 'lines' | 'none';

export interface ChartLook {
  /** Готовый вид, от которого взят фон. */
  readonly preset: string;
  readonly up: string;
  readonly down: string;
  /** Падающие свечи контуром: рост и падение различимы не только цветом. */
  readonly hollow: boolean;
  readonly grid: LookGrid;
  /** Яркость объёма, 0.1–0.6. */
  readonly volume: number;
  /** Своя картинка вместо фона вида. */
  readonly image: boolean;
  /** Затемнение своей картинки, 0–0.9: без него свечи на фото не читаются. */
  readonly dim: number;
}

export interface LookPreset {
  readonly id: string;
  readonly name: string;
  readonly text: string;
  /** CSS-фон контейнера графика. */
  readonly backdrop: string;
  readonly up: string;
  readonly down: string;
  readonly grid: LookGrid;
  readonly gridColor: string;
  readonly volume: number;
}

export const LOOK_PRESETS: readonly LookPreset[] = [
  {
    id: 'depth',
    name: 'Глубина',
    text: 'Светлее сверху, темнее к низу. Сетка точками, объём приглушён.',
    backdrop: 'radial-gradient(120% 90% at 50% 0%, #121a44 0%, #0a0f2a 46%, #060818 100%)',
    up: '#35d6a0',
    down: '#f25c7c',
    grid: 'dots',
    gridColor: 'rgba(150, 165, 255, 0.16)',
    volume: 0.22,
  },
  {
    id: 'night',
    name: 'Ночь',
    text: 'Плоский тёмно-синий фон и яркие свечи — прежний вид графика.',
    backdrop: '#060b15',
    up: '#2ee6b0',
    down: '#ff5470',
    grid: 'lines',
    gridColor: 'rgba(18, 28, 44, 1)',
    volume: 0.42,
  },
  {
    id: 'amethyst',
    name: 'Аметист',
    text: 'Фирменные цвета вместо биржевых: рост бирюзовый, падение малиновое.',
    backdrop: 'linear-gradient(180deg, #151036 0%, #0b0822 55%, #070514 100%)',
    up: '#4fe3ff',
    down: '#ff5fa2',
    grid: 'dots',
    gridColor: 'rgba(180, 150, 255, 0.15)',
    volume: 0.24,
  },
  {
    id: 'graphite',
    name: 'Графит',
    text: 'Почти чёрный нейтральный фон без синевы — самый строгий вид.',
    backdrop: 'linear-gradient(180deg, #10131a 0%, #090b10 100%)',
    up: '#22c58b',
    down: '#ef4d68',
    grid: 'lines',
    gridColor: 'rgba(255, 255, 255, 0.045)',
    volume: 0.26,
  },
  {
    id: 'ocean',
    name: 'Океан',
    text: 'Глубокий сине-зелёный: спокойный фон для долгой работы.',
    backdrop: 'radial-gradient(130% 100% at 20% 0%, #0d3040 0%, #071c2a 48%, #040d16 100%)',
    up: '#5eead4',
    down: '#fb7185',
    grid: 'dots',
    gridColor: 'rgba(120, 220, 230, 0.14)',
    volume: 0.22,
  },
  {
    id: 'sunset',
    name: 'Закат',
    text: 'Тёплый тёмный фон с бордовым отливом; рост золотой, падение алое.',
    backdrop: 'linear-gradient(180deg, #2a1230 0%, #170a1c 52%, #0a050d 100%)',
    up: '#ffc861',
    down: '#ff5d6c',
    grid: 'dots',
    gridColor: 'rgba(255, 190, 150, 0.13)',
    volume: 0.22,
  },
  {
    id: 'emerald',
    name: 'Изумруд',
    text: 'Тёмно-зелёный фон, свечи мятные и коралловые.',
    backdrop: 'radial-gradient(120% 90% at 50% 0%, #0f2e25 0%, #081a16 50%, #040c0a 100%)',
    up: '#6ee7b7',
    down: '#ff7a8a',
    grid: 'dots',
    gridColor: 'rgba(140, 230, 190, 0.13)',
    volume: 0.22,
  },
  {
    id: 'mono',
    name: 'Монохром',
    text: 'Без цвета: рост светлый, падение серым контуром. Ничего не отвлекает.',
    backdrop: 'linear-gradient(180deg, #14161c 0%, #0a0b0f 100%)',
    up: '#e6e9f2',
    down: '#7c8498',
    grid: 'none',
    gridColor: 'rgba(255, 255, 255, 0.05)',
    volume: 0.2,
  },
];

export const DEFAULT_PRESET = 'night';

export function presetOf(id: string): LookPreset {
  return LOOK_PRESETS.find((entry) => entry.id === id) ?? LOOK_PRESETS[0]!;
}

/** Вид «как в готовом»: фон, цвета, сетка и объём пресета, без своей картинки. */
export function lookOfPreset(id: string): ChartLook {
  const preset = presetOf(id);
  return { preset: preset.id, up: preset.up, down: preset.down, hollow: preset.id === 'mono', grid: preset.grid, volume: preset.volume, image: false, dim: 0.55 };
}

export const DEFAULT_LOOK: ChartLook = lookOfPreset(DEFAULT_PRESET);

const HEX = /^#[0-9a-f]{6}$/i;
const clamp = (value: unknown, min: number, max: number, fallback: number): number => (typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback);

/** Разбор сохранённого вида: профиль живёт дольше кода, чужое или старое поле не должно ронять график. */
export function parseLook(value: unknown): ChartLook {
  if (typeof value !== 'object' || value === null) return DEFAULT_LOOK;
  const raw = value as Partial<Record<keyof ChartLook, unknown>>;
  const base = lookOfPreset(typeof raw.preset === 'string' ? raw.preset : DEFAULT_PRESET);
  return {
    preset: base.preset,
    up: typeof raw.up === 'string' && HEX.test(raw.up) ? raw.up : base.up,
    down: typeof raw.down === 'string' && HEX.test(raw.down) ? raw.down : base.down,
    hollow: typeof raw.hollow === 'boolean' ? raw.hollow : base.hollow,
    grid: raw.grid === 'dots' || raw.grid === 'lines' || raw.grid === 'none' ? raw.grid : base.grid,
    volume: clamp(raw.volume, 0.1, 0.6, base.volume),
    image: raw.image === true,
    dim: clamp(raw.dim, 0, 0.9, base.dim),
  };
}

function alpha(hex: string, share: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${share})`;
}

/**
 * Применить вид к палитре и вернуть опции для `chart.applyOptions`.
 *
 * Холст прозрачен всегда: фон рисует контейнер (см. `backdropOf`).
 */
export function applyLook(look: ChartLook): Record<string, unknown> {
  const preset = presetOf(look.preset);
  palette.up = look.up;
  palette.down = look.down;
  palette.upSoft = alpha(look.up, look.volume);
  palette.downSoft = alpha(look.down, look.volume);
  palette.grid = preset.gridColor;
  const line = { color: preset.gridColor, visible: look.grid !== 'none', style: look.grid === 'dots' ? 4 : 0 };
  return {
    layout: { background: { color: 'transparent' } },
    grid: { vertLines: line, horzLines: line },
  };
}

/** Цвета свечей для вида: у «контуром» тело падающей свечи прозрачно. */
export function lookCandleColors(look: ChartLook): Record<string, string> {
  return {
    upColor: look.up,
    downColor: look.hollow ? 'rgba(0, 0, 0, 0)' : look.down,
    borderUpColor: look.up,
    borderDownColor: look.down,
    wickUpColor: look.up,
    wickDownColor: look.down,
  };
}

/** CSS-фон контейнера графика: фон вида или своя картинка под затемнением. */
export function backdropOf(look: ChartLook, image: string | null): string {
  if (look.image && image) {
    const shade = `rgba(4, 6, 16, ${look.dim})`;
    return `linear-gradient(${shade}, ${shade}), url("${image}") center / cover no-repeat`;
  }
  return presetOf(look.preset).backdrop;
}

/* ------------------------------------------------------------ своя картинка */

const IMAGE_PREFIX = 'anzonix.chart-backdrop.';
/** Картинка ужимается до этой стороны: фон графика не галерея, а хранилище браузера невелико. */
const IMAGE_SIDE = 1920;
const IMAGE_QUALITY = 0.82;
const IMAGE_EVENT = 'anzonix:chart-backdrop';

const imageKey = (owner: string): string => `${IMAGE_PREFIX}${owner}`;

export function readBackdropImage(owner: string): string | null {
  try {
    return localStorage.getItem(imageKey(owner));
  } catch {
    return null;
  }
}

export function clearBackdropImage(owner: string): void {
  try {
    localStorage.removeItem(imageKey(owner));
  } catch {
    // Хранилище закрыто — убирать нечего.
  }
  window.dispatchEvent(new Event(IMAGE_EVENT));
}

/** Сохранить картинку человека: ужать, перевести в JPEG и положить в браузер. Возвращает текст ошибки или `null`. */
export async function saveBackdropImage(owner: string, file: File): Promise<string | null> {
  if (!file.type.startsWith('image/')) return 'Это не картинка. Подойдут JPG, PNG и WebP.';
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return 'Картинка не открылась — попробуйте другой файл.';
  }
  const scale = Math.min(1, IMAGE_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext('2d');
  if (!context) return 'Браузер не дал обработать картинку.';
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  try {
    localStorage.setItem(imageKey(owner), canvas.toDataURL('image/jpeg', IMAGE_QUALITY));
  } catch {
    return 'В браузере не хватило места для картинки. Возьмите файл поменьше.';
  }
  window.dispatchEvent(new Event(IMAGE_EVENT));
  return null;
}

/** Подписка на смену своей картинки: её ставят в окне настроек, а рисует контейнер графика. */
export function onBackdropImage(listener: () => void): () => void {
  window.addEventListener(IMAGE_EVENT, listener);
  return () => window.removeEventListener(IMAGE_EVENT, listener);
}
